<template>
  <TableList
    v-rect-loading="loading"
    class="open-question-template-group-list"
    row-key="_id"
    default-expand-all
    :title="$t(`${i18nPrefix}.template.title`)"
    :row-class-name="getRowClassName"
    :listData="groupListSearchResult"
    :tableColumns="tableColumns"
    :dragSortFunc="dragSortFunc"
    :span-method="spanMethod"
    @action="handleEdit"
  >
    <template #filter>
      <el-select
        v-model="search.groupId"
        filterable
        clearable
        class="group-filter"
        :placeholder="$t(`${i18nPrefix}.placeholder.groupFilter`)"
        @change="searchGroupList"
      >
        <el-option
          v-for="(item, index) in groupList"
          :key="index"
          :label="item.i18n_name"
          :value="item.id"
        />
      </el-select>
    </template>
    <template #btn>
      <el-button class="create-group-btn create-btn" @click="toTaskHistoryPage">{{ $t(`${i18nPrefix}.btn.taskList`) }}</el-button>
      <el-button class="create-group-btn create-btn" @click="openAddGroupDialog">{{ $t(`${i18nPrefix}.btn.addNewGroup`) }}</el-button>
      <el-button class="create-question-btn create-btn" @click="openAddQuestionDialog">{{ $t(`${i18nPrefix}.btn.addNewQuestion`) }}</el-button>
    </template>
    <template #groupName="{row, formattedData}">
      <div class="other-group" v-if="row.id === -1">
        <span class="title">{{ $t(`${i18nPrefix}.label.otherGroup`) }}</span>
      </div>
      <span v-else>{{ formattedData }}</span>
    </template>
    <template #draggable="{row}">
      <div class="drag-btn">
        <i v-if="row._vType === DATA_TYPE.QUESTION" class="iconfont iconMore draggable-handle"></i>
      </div>
    </template>
    <template #status="{ data, row }">
      <el-switch :value="data === 1" @change="handleStatusChange(row)"></el-switch>
    </template>
    <template #page-bottom>
      <DialogWrapper
        class="editor-dialog editor-dialog"
        :loading="groupEditor.loading"
        :show.sync="groupEditor.show"
        :close-on-click-modal="false"
        :title="$t(`${i18nPrefix}.editor.group.title.${groupEditor.isEdit ? 'edit' : 'add'}`)"
        @cancel="handleCancelGroup"
        @save="handleSaveGroup"
      >
        <GroupEditor ref="groupEditor" :is-edit="groupEditor.isEdit" :group="groupEditor.group"></GroupEditor>
      </DialogWrapper>
      <DialogWrapper
        class="editor-dialog question-editor"
        :loading="questionEditor.loading"
        :show.sync="questionEditor.show"
        :close-on-click-modal="false"
        :title="$t(`${i18nPrefix}.editor.question.title.${questionEditor.isEdit ? 'edit' : 'add'}`)"
        @cancel="handleCancelQuestion"
        @save="handleSaveQuestion"
      >
        <QuestionEditor
          ref="questionEditor"
          is-template
          :is-edit="questionEditor.isEdit"
          :question="questionEditor.question"
          :disabledQuestionType="disabledQuestionType"
          :group-list="groupList"
          :showUpdateButton="true"
        />
      </DialogWrapper>
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import GroupEditor from '@/components/openQuestion/groupEditor.vue'
import QuestionEditor from '@/components/openQuestion/questionEditor.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { QUESTION_TYPE, DATA_TYPE } from '@/constant/openQuestion'
import { i18nPrefix, parseGroup, parseQuestion } from '@/components/openQuestion/common'

export default {
  components: {
    TableList,
    GroupEditor,
    QuestionEditor,
    DialogWrapper
  },
  data () {
    return {
      i18nPrefix,
      DATA_TYPE,
      loading: false,
      groupList: [],
      groupListSearchResult: [],
      search: {
        groupId: null
      },
      groupEditor: {
        loading: false,
        show: false,
        isEdit: false,
        group: null
      },
      questionEditor: {
        loading: false,
        show: false,
        isEdit: false,
        question: null
      }
    }
  },
  computed: {
    tableColumns () {
      const vm = this
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.groupName`),
          slotName: 'groupName',
          dataKey: 'i18n_name',
          formatter: (data, row) => {
            if (row._vType === DATA_TYPE.GROUP) {
              return data
            }
          }
        },
        {
          width: 60,
          label: this.$t(`${this.i18nPrefix}.label.questionType`),
          dataKey: 'type',
          formatter: (data, row) => {
            if (row._vType === DATA_TYPE.QUESTION) {
              const type = _.findKey(QUESTION_TYPE, item => item === data)
              return vm.$t(`${vm.i18nPrefix}.questionType.${type}`)
            }
          }
        },
        {
          width: 200,
          label: this.$t(`${this.i18nPrefix}.label.questionTitle`),
          dataKey: 'title',
          formatter: (data, row) => {
            if (row._vType === DATA_TYPE.QUESTION) {
              return data
            }
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'status',
          slotName: 'status',
          width: 50
        },
        {
          label: this.$t('common.label.sortOperate'),
          isDragSort: true,
          slotName: 'draggable',
          width: 50
        }
      ]
      return columns
    },
    flatGroupQuestionList () {
      const flatList = []
      _.forEach(this.groupListSearchResult, group => {
        flatList.push(group)
        flatList.push(...group.children)
      })
      return flatList
    },
    hasVideoQuestionPermission () {
      return this.hasAnyPermission('PREMIUM_UPLOAD_VIDEO')
    },
    hasAttachmentQuestionPermission () {
      return this.hasAnyPermission('PREMIUM_UPLOAD_PORTFOLIO')
    },
    disabledQuestionType () {
      const disabledQuestionType = []
      if (!this.hasVideoQuestionPermission) {
        disabledQuestionType.push(QUESTION_TYPE.VIDEO)
      }
      if (!this.hasAttachmentQuestionPermission) {
        disabledQuestionType.push(QUESTION_TYPE.ATTACHMENT)
      }
      return disabledQuestionType
    }
  },
  created () {
    this.fetchGroupList()
  },
  mounted () {

  },
  methods: {
    ...mapActions('openQuestion', [
      'getQuestionTemplateGroupList',
      'createQuestionTemplateGroup',
      'updateQuestionTemplateGroup',
      'createQuestionTemplate',
      'updateQuestionTemplate',
      'moveGroupQuestionTemplate'
    ]),
    dragSortFunc (newIndex, oldIndex, tableSorter) {
      if (newIndex === oldIndex) {
        return
      }
      if (newIndex === 0) {
        const sortItems = tableSorter.toArray()
        const movedItem = sortItems.splice(newIndex, 1)
        sortItems.splice(oldIndex, 0, movedItem[0])
        tableSorter.sort(sortItems)
        return
      }
      const start = oldIndex < newIndex ? oldIndex : newIndex
      const end = oldIndex > newIndex ? oldIndex : newIndex
      const changedList = this.flatGroupQuestionList.filter((item, index) => index >= start - 1 && index <= end)
      const oldData = this.flatGroupQuestionList[oldIndex]
      const newData = this.flatGroupQuestionList[newIndex]
      const oldDataIndex = changedList.findIndex(item => item._id === oldData._id)
      const newDataIndex = changedList.findIndex(item => item._id === newData._id)
      changedList.splice(oldDataIndex, 1)
      changedList.splice(newDataIndex, 0, oldData)
      const beforeData = changedList[newDataIndex - 1]
      const targetGroup = beforeData._vType === DATA_TYPE.GROUP ? beforeData : beforeData._parent
      const moveTo = beforeData._vType === DATA_TYPE.GROUP ? 1 : beforeData.order + 1
      const question = oldData
      this.handleMove(targetGroup, question, moveTo)
    },
    getRowClassName ({ row }) {
      return {
        'un-group-row': row.id === -1,
        'group-row': row._vType === DATA_TYPE.GROUP,
        [`${row._id}`]: true
      }
    },
    async fetchGroupList () {
      this.loading = true
      try {
        const resp = await this.getQuestionTemplateGroupList()
        if (resp) {
          this.groupList = resp.map(parseGroup)
          this.searchGroupList()
        }
      } finally {
        this.loading = false
      }
    },
    searchGroupList () {
      if (this.search.groupId) {
        const group = _.find(this.groupList, { id: this.search.groupId })
        this.groupListSearchResult = [group]
      } else {
        this.groupListSearchResult = _.map(this.groupList)
      }
    },
    toTaskHistoryPage () {
      this.$router.push('/v-admin/open-question/template/management/task/list')
    },
    openAddGroupDialog () {
      this.groupEditor.isEdit = false
      this.groupEditor.group = null
      if (this.$refs.groupEditor) {
        this.$refs.groupEditor.clearData()
      }
      this.groupEditor.show = true
    },
    openAddQuestionDialog () {
      this.questionEditor.isEdit = false
      this.questionEditor.question = null
      if (this.$refs.questionEditor) {
        this.$refs.questionEditor.clearData()
      }
      this.questionEditor.show = true
    },
    handleEdit (row) {
      if (row._vType === DATA_TYPE.QUESTION) {
        this.questionEditor.isEdit = true
        this.questionEditor.question = row
        this.questionEditor.show = true
      } else {
        this.groupEditor.isEdit = true
        this.groupEditor.group = row
        this.groupEditor.show = true
      }
    },
    async handleStatusChange (row) {
      const updateFunc = row._vType === DATA_TYPE.QUESTION ? this.updateQuestionTemplate : this.updateQuestionTemplateGroup
      this.$eventBus.$emit('showLoading', true)
      try {
        const newStatus = row.status === 1 ? 0 : 1
        const data = {
          questionId: row.id,
          data: {
            status: newStatus
          }
        }
        const resp = await updateFunc(data)
        if (resp) {
          row.status = resp.status
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async handleMove (group, question, moveTo) {
      if (moveTo === -1) {
        moveTo = group.children.length + 1
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        const parent = question._parent
        const resp = await this.moveGroupQuestionTemplate({
          fromGroup: parent,
          toGroup: group,
          question,
          moveTo
        })
        if (resp) {
          const parentQuestion = resp.from_group.question_templates
          parent.question_templates = parentQuestion
          parent.children = parentQuestion.map((child) => parseQuestion(child, parent))
          const groupQuestion = resp.to_group.question_templates
          groupQuestion.question_templates = groupQuestion
          group.children = groupQuestion.map((child) => parseQuestion(child, group))
        }
        this.searchGroupList()
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    handleCancelGroup () {
      this.groupEditor.show = false
    },
    async handleSaveGroup () {
      this.groupEditor.loading = true
      try {
        const groupEditor = this.groupEditor
        if (!groupEditor.show) {
          return
        }
        const groupEditorRef = this.$refs.groupEditor
        const groupData = await groupEditorRef.submit()
        if (!groupData) {
          return
        }
        let data = groupData
        let saveFunc = this.createQuestionTemplateGroup
        if (groupEditor.isEdit) {
          data = {
            questionId: groupData.id,
            data: groupData
          }
          saveFunc = this.updateQuestionTemplateGroup
        }
        const resp = await saveFunc(data)
        if (resp) {
          if (groupEditor.isEdit) {
            const group = this.groupEditor.group
            group.name = resp.name
            group.cn_name = resp.name
          } else {
            const group = parseGroup(resp)
            this.groupList.splice(0, 0, group)
          }
          groupEditor.show = false
        }
        this.searchGroupList()
      } finally {
        this.groupEditor.loading = false
      }
    },
    handleCancelQuestion () {
      this.questionEditor.show = false
    },
    async handleSaveQuestion () {
      this.questionEditor.loading = true
      try {
        const questionEditor = this.questionEditor
        if (!questionEditor.show) {
          return
        }
        const questionEditorRef = this.$refs.questionEditor
        const questionData = await questionEditorRef.submit()
        if (!questionData) {
          return
        }
        const group = _.find(this.groupList, { id: questionData.group_id })
        let data = questionData
        let saveFunc = this.createQuestionTemplate
        if (questionEditor.isEdit) {
          data = {
            questionId: questionData.id,
            data: questionData
          }
          saveFunc = this.updateQuestionTemplate
        }
        const resp = await saveFunc(data)
        if (resp) {
          const respQuestion = parseQuestion(resp, group)
          if (questionEditor.isEdit) {
            const question = this.questionEditor.question
            if (question._parent.id !== group.id) {
              const parent = question._parent
              const index = parent.children.findIndex(item => item.id === question.id)
              parent.children.splice(index, 1)
              parent.question_templates.splice(index, 1)
              group.question_templates.push(resp)
              group.children.push(respQuestion)
            }
            _.assign(question, respQuestion)
          } else {
            group.question_templates.push(resp)
            group.children.push(respQuestion)
          }
          questionEditor.show = false
        }
        this.searchGroupList()
      } finally {
        this.questionEditor.loading = false
      }
    },
    spanMethod ({ row, columnIndex }) {
      if (row.id !== -1) {
        return
      }
      if (columnIndex === 0) {
        return {
          rowspan: 1,
          colspan: 6
        }
      } else {
        return {
          rowspan: 0,
          colspan: 0
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.open-question-template-group-list {
  .other-group {
    text-align: center;

    .title {
      margin: 0 20px;
    }
  }

  ::v-deep {
    .el-table {
      td.operate-btn-group {
        .cell {
          padding: 0 10px;
          display: flex;
          justify-content: space-between;
        }
      }

      .un-group-row {
        background-color: #fafafa;

        td.el-table__cell {
          &:hover {
            background-color: #fafafa;
          }

          .el-table__expand-icon {
            display: none;
          }
        }
      }
    }

    .btn-container {
      display: flex;
    }
  }

  .drag-btn {
    .draggable-handle {
      cursor: pointer;
    }
  }

  .group-row {
    .drag-btn {
      visibility: hidden;
    }
  }
}

</style>
